/**
 * DuplicatePatientBanner — extraction-tab banner that surfaces patient
 * duplicate-check status (matches found, no matches, decision confirmed) and
 * keeps the box stable during in-flight re-checks.
 *
 * Visibility (the parent decides whether to render us at all): only mounted
 * when at least one duplicate-check backend is configured (see `usePatientMatching`'s
 * `duplicateCheckReady`) — we render unconditionally inside.
 *
 * Color rules:
 *   - Green: a patient decision is committed (new/existing patient confirmed)
 *     OR the latest completed duplicate check found no matches.
 *   - Amber: the latest completed check found matches but no decision has been
 *     made yet, or no matches were found and the user must explicitly confirm
 *     "new patient".
 *   - Blue: a duplicate check is in flight and the current visible state is an
 *     uncommitted search result. Committed patient decisions stay visible with
 *     a small inline spinner because the decision is still relevant.
 */
import React from 'react';
import { Alert, AlertDescription } from '@roger/ui-components';
import { Loader2, Users, CheckCircle, type LucideIcon } from 'lucide-react';
import type { IntakeResponse } from '../../services/api';
import type { PatientMatchResult } from '../../hooks/useCreatePatientForm/types';

type BannerVariant = 'green' | 'amber' | 'blue';

interface VariantStyles {
  alert: string;
  hover: string;
  text: string;
  icon: string;
  link: string;
  recheck: string;
  Icon: LucideIcon;
}

const PALETTE: Record<BannerVariant, VariantStyles> = {
  green: {
    alert: 'border-green-200 bg-green-50',
    hover: 'hover:bg-green-100',
    text: 'text-green-800',
    icon: 'text-green-600',
    link: 'text-green-600',
    recheck: 'text-green-700',
    Icon: CheckCircle,
  },
  amber: {
    alert: 'border-amber-200 bg-amber-50',
    hover: 'hover:bg-amber-100',
    text: 'text-amber-800',
    icon: 'text-amber-600',
    link: 'text-amber-600',
    recheck: 'text-amber-700',
    Icon: Users,
  },
  blue: {
    alert: 'border-sky-200 bg-sky-50',
    hover: 'hover:bg-sky-100',
    text: 'text-sky-800',
    icon: 'text-sky-600',
    link: 'text-sky-600',
    recheck: 'text-sky-700',
    Icon: Loader2,
  },
};

export interface DuplicatePatientBannerProps {
  /** Just the intake fields we read; accepts the full IntakeResponse. */
  intake: Pick<IntakeResponse, 'is_new_patient'>;
  patientMatches: PatientMatchResult[];
  isMatchingPatients: boolean;
  /** Agency enables Kinnser and/or Roger EMR for intake duplicate checks. */
  allowSelectExistingPatient: boolean;
  onOpenMatchModal: () => void;
  onConfirmNewPatient: () => void | Promise<void>;
}

export const DuplicatePatientBanner: React.FC<DuplicatePatientBannerProps> = ({
  intake,
  patientMatches,
  isMatchingPatients,
  allowSelectExistingPatient,
  onOpenMatchModal,
  onConfirmNewPatient,
}) => {
  const isNewPatient = intake.is_new_patient;
  const hasMatchingPatients = patientMatches.length > 0;

  const decisionMade = isNewPatient === true || isNewPatient === false;
  // When the existing-patient FF is off we auto-mark as new on first match;
  // surface that as "potential duplicates" rather than a confirmed decision.
  const showAsPotentialMatches =
    decisionMade && isNewPatient === true && !allowSelectExistingPatient;
  const effectiveDecisionMade = decisionMade && !showAsPotentialMatches;
  const showConfirmedDecision =
    effectiveDecisionMade &&
    (hasMatchingPatients || isNewPatient === true || isMatchingPatients);

  // Each downstream render branch corresponds to exactly one of these
  // variants, derived purely from data state (ignoring any in-flight check).
  const baseVariant: BannerVariant = (() => {
    if (hasMatchingPatients) return showConfirmedDecision ? 'green' : 'amber';
    if (!allowSelectExistingPatient) return 'green';
    if (showConfirmedDecision) return 'green';
    return 'amber';
  })();

  // While a duplicate check is in flight, hide uncommitted search results from
  // the previous request. Keep explicit patient decisions visible because they
  // are still relevant independently of the latest search result.
  const preserveDuringRecheck = effectiveDecisionMade;
  const variant: BannerVariant =
    isMatchingPatients && !preserveDuringRecheck ? 'blue' : baseVariant;

  const p = PALETTE[variant];
  const Icon = p.Icon;

  if (variant === 'blue') {
    return (
      <Alert className={p.alert}>
        <AlertDescription className={`${p.text} flex gap-2 items-center`}>
          <Icon className={`h-4 w-4 ${p.icon} animate-spin`} />
          Checking for duplicate patients...
        </AlertDescription>
      </Alert>
    );
  }

  const inlineRecheck = isMatchingPatients ? (
    <span className={`ml-auto flex items-center gap-1.5 text-xs ${p.recheck}`}>
      <Loader2 className='h-3 w-3 animate-spin' />
      Checking for duplicate patients…
    </span>
  ) : null;

  // Branch 1: matches present (clickable → opens match modal)
  if (hasMatchingPatients) {
    const bannerText = effectiveDecisionMade
      ? isNewPatient
        ? 'New patient confirmed'
        : 'Existing patient confirmed'
      : `${patientMatches.length} potential duplicate${patientMatches.length > 1 ? 's' : ''} found`;
    return (
      <Alert
        className={`${p.alert} cursor-pointer ${p.hover} transition-colors`}
        onClick={onOpenMatchModal}
      >
        <AlertDescription className={`${p.text} flex gap-2 items-center`}>
          <Icon className={`h-4 w-4 ${p.icon}`} />
          <span className='font-medium'>{bannerText}</span>
          {inlineRecheck ?? (
            <span className={`${p.link} underline ml-auto`}>
              View potential matches
            </span>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Branch 2: no matches, FF off → green "No duplicates" (auto-confirmed)
  if (!allowSelectExistingPatient) {
    return (
      <Alert className={p.alert}>
        <AlertDescription
          className={`${p.text} flex gap-2 items-center w-full`}
        >
          <Icon className={`h-4 w-4 ${p.icon}`} />
          <span>No duplicate patients found</span>
          {inlineRecheck}
        </AlertDescription>
      </Alert>
    );
  }

  // Branch 3: no matches, FF on, decision already made
  if (showConfirmedDecision) {
    return (
      <Alert className={p.alert}>
        <AlertDescription
          className={`${p.text} flex gap-2 items-center w-full`}
        >
          <Icon className={`h-4 w-4 ${p.icon}`} />
          <span className='font-medium'>
            {isNewPatient
              ? 'New patient confirmed'
              : 'Existing patient confirmed'}
          </span>
          {inlineRecheck}
        </AlertDescription>
      </Alert>
    );
  }

  // Branch 4: no matches, FF on, decision pending → amber prompt to confirm.
  return (
    <Alert className={p.alert}>
      <AlertDescription className={`${p.text} flex gap-2 items-center w-full`}>
        <Icon className={`h-4 w-4 ${p.icon} flex-shrink-0`} />
        <span className='font-medium'>No duplicate patients found</span>
        <button
          type='button'
          className={`${p.link} underline ml-auto text-sm font-medium bg-transparent border-0 cursor-pointer p-0`}
          onClick={e => {
            e.stopPropagation();
            void onConfirmNewPatient();
          }}
        >
          Confirm New Patient
        </button>
      </AlertDescription>
    </Alert>
  );
};
